import { z } from "zod";
import { ROUTES } from "@/app/routes";

/// Where a login started from an AI app's Connect may send the person
/// afterwards. Byte for byte the shore's `isSafeOAuthReturnTo` pattern: only
/// the consent page with a 32 character request id, so a crafted link can
/// never turn the login screen into an open redirect.
const CONSENT_RETURN_TO = /^\/oauth\/consent\?request=([0-9A-Za-z]{32})$/;

export function isSafeOAuthReturnTo(value: unknown): value is string {
  return typeof value === "string" && CONSENT_RETURN_TO.test(value);
}

export function consentReturnTo(requestId: string): string {
  return `${ROUTES.oauthConsent.path}?request=${requestId}`;
}

export function consentRequestIdOf(returnTo: string): string | null {
  return CONSENT_RETURN_TO.exec(returnTo)?.[1] ?? null;
}

/// Marks "this browser started connecting an AI app". The emailed magic link
/// carries the consent return-to, and the verify page only follows it when
/// the marker is here: opened on another device, the link would otherwise
/// sign in that device and show a consent screen whose approval returns to an
/// app running somewhere else.
const PENDING_CONSENT_STORAGE_KEY = "pw_oauth_pending_request";
const PENDING_CONSENT_TTL_MS = 10 * 60 * 1000;

const pendingConsentMarkerSchema = z.object({ requestId: z.string(), expiresAt: z.number() });
type PendingConsentMarker = z.infer<typeof pendingConsentMarkerSchema>;

export function rememberPendingConsent(requestId: string, now: number = Date.now()): void {
  const marker: PendingConsentMarker = { requestId, expiresAt: now + PENDING_CONSENT_TTL_MS };
  try {
    localStorage.setItem(PENDING_CONSENT_STORAGE_KEY, JSON.stringify(marker));
  } catch {
    // Storage disabled or full: the verify page then treats the link as
    // opened elsewhere and offers the 6-digit code, which still works.
  }
}

export function hasPendingConsent(requestId: string, now: number = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(PENDING_CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const marker = pendingConsentMarkerSchema.safeParse(JSON.parse(raw));
    return marker.success && marker.data.requestId === requestId && marker.data.expiresAt > now;
  } catch {
    return false;
  }
}

/// The consent page this browser was last sending the person to, if the
/// marker is still fresh. Signup and password reset leave the consent URL, so
/// they use this to come back instead of carrying the request in a URL.
export function pendingConsentReturnTo(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(PENDING_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const marker = pendingConsentMarkerSchema.safeParse(JSON.parse(raw));
    return marker.success && marker.data.expiresAt > now
      ? consentReturnTo(marker.data.requestId)
      : null;
  } catch {
    return null;
  }
}

/// Called right before a sign-in step that leaves this page (emailing a
/// magic link, starting Google), so the way back can tell it is still the
/// same browser.
export function rememberConsentReturnTo(returnTo: string): void {
  const requestId = consentRequestIdOf(returnTo);
  if (requestId) rememberPendingConsent(requestId);
}
